import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { FaSave, FaChevronLeft } from 'react-icons/fa';

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// Reproduces CreateOrEditSaleVoucherItemFormController's exact tax math (verified
// against the real, working Pro360 mobile app source - not guessed):
// - Without tax inclusive: vat = credit * (rate/100); total = credit + vat.
// - With tax inclusive: the entered credit is the tax-inclusive total. total stays
//   as entered, the credit field itself is rewritten to the net (tax-exclusive)
//   amount = entered / (1 + rate/100), and vat = total - that net amount.
const calcWithoutTaxInclusive = (credit, ratePercent) => {
  const vat = credit * (ratePercent / 100);
  return { credit, vat, total: credit + vat };
};
const calcWithTaxInclusive = (enteredCredit, ratePercent) => {
  const total = enteredCredit;
  const netCredit = enteredCredit / (1 + ratePercent / 100);
  const vat = total - netCredit;
  return { credit: netCredit, vat, total };
};

const SaleVoucherItemForm = ({
  show,
  onClose,
  onSave,
  editItem,
  accounts = [],
  taxes = [],
  costCenter1 = [],
  costCenter2 = [],
  costCenter3 = [],
  costCenter4 = [],
  headerCostCenters,
  taxInclusive = false,
  applySameCostCenters = true,
  defaultNarration = '',
}) => {
  const emptyForm = () => ({
    account: null,
    tax: null,
    credit: '',
    vat: '0.00',
    narration: defaultNarration,
    cc1: headerCostCenters?.cc1 || null,
    cc2: headerCostCenters?.cc2 || null,
    cc3: headerCostCenters?.cc3 || null,
    cc4: headerCostCenters?.cc4 || null,
  });
  const [form, setForm] = useState(emptyForm());
  const [total, setTotal] = useState(0);

  const accountOptions = accounts?.map((a) => ({ value: a?.id, label: a?.name, taxCode: a?.taxCode })) || [];
  const taxOptions = taxes?.map((t) => ({ value: t?.recordId, label: t?.description, rate: t?.rate, raw: t })) || [];

  useEffect(() => {
    if (!show) return;
    if (editItem) {
      setForm({
        account: { value: editItem.accountCode, label: editItem.accountName },
        tax: taxOptions.find((t) => t.value === editItem.taxCode) || null,
        credit: editItem.amountCredit != null ? String(editItem.amountCredit) : '',
        vat: editItem.vat != null ? Number(editItem.vat).toFixed(2) : '0.00',
        narration: editItem.narration || '',
        cc1: costCenter1.find((c) => c.id === editItem.costCenter1) ? { value: editItem.costCenter1, label: editItem.costCenter1 } : headerCostCenters?.cc1 || null,
        cc2: costCenter2.find((c) => c.id === editItem.costCenter2) ? { value: editItem.costCenter2, label: editItem.costCenter2 } : headerCostCenters?.cc2 || null,
        cc3: costCenter3.find((c) => c.id === editItem.costCenter3) ? { value: editItem.costCenter3, label: editItem.costCenter3 } : headerCostCenters?.cc3 || null,
        cc4: costCenter4.find((c) => c.id === editItem.costCenter4) ? { value: editItem.costCenter4, label: editItem.costCenter4 } : headerCostCenters?.cc4 || null,
      });
      setTotal(editItem.total || 0);
    } else {
      setForm(emptyForm());
      setTotal(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, editItem]);

  const handleAccountSelected = (option) => {
    const matchedTax = taxOptions.find((t) => t.value === option?.taxCode) || null;
    setForm((prev) => ({ ...prev, account: option, tax: matchedTax || prev.tax }));
    if (form.credit) recalc(form.credit, matchedTax || form.tax);
  };

  const recalc = (creditValue, taxOption) => {
    const credit = parseFloat(creditValue) || 0;
    const rate = taxOption?.rate || 0;
    const result = taxInclusive ? calcWithTaxInclusive(credit, rate) : calcWithoutTaxInclusive(credit, rate);
    setForm((prev) => ({ ...prev, credit: result.credit.toFixed(2), vat: result.vat.toFixed(2) }));
    setTotal(result.total);
  };

  const handleCreditBlur = () => {
    recalc(form.credit, form.tax);
  };

  const handleTaxSelected = (option) => {
    setForm((prev) => ({ ...prev, tax: option }));
    recalc(form.credit, option);
  };

  const handleVatChanged = (value) => {
    const credit = parseFloat(form.credit) || 0;
    const vat = parseFloat(value) || 0;
    setForm((prev) => ({ ...prev, vat: value }));
    setTotal(credit + vat);
  };

  const handleSave = () => {
    if (!form.account) {
      toast.warning('Please select account code');
      return;
    }
    if (!form.credit || parseFloat(form.credit) <= 0) {
      toast.warning('Please enter a valid credit amount');
      return;
    }

    onSave({
      accountCode: form.account.value,
      accountName: form.account.label,
      taxCode: form.tax?.value || 0,
      taxName: form.tax?.label || '',
      amountCredit: parseFloat(form.credit) || 0,
      vat: parseFloat(form.vat) || 0,
      total,
      narration: form.narration || '',
      costCenter1: applySameCostCenters ? headerCostCenters?.cc1?.value || '' : form.cc1?.value || '',
      costCenter2: applySameCostCenters ? headerCostCenters?.cc2?.value || '' : form.cc2?.value || '',
      costCenter3: applySameCostCenters ? headerCostCenters?.cc3?.value || '' : form.cc3?.value || '',
      costCenter4: applySameCostCenters ? headerCostCenters?.cc4?.value || '' : form.cc4?.value || '',
      vatAccountCode: form.tax?.raw?.vATAcCode || form.tax?.raw?.vatAcCode || '0',
      stAccountCode: form.tax?.raw?.sTAcCode || form.tax?.raw?.stAcCode || '0',
    });
  };

  return (
    <Modal show={show} onHide={onClose} backdrop="static" keyboard={false} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{editItem ? 'Update Item' : 'Add Item'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* This modal is too narrow for the page-level .grid-contain class
            (forces 2-3 fixed-width columns based on viewport, not this modal's
            width, which clipped/overlapped fields) - a simple responsive flex
            wrap fits a compact popup like this one correctly instead. */}
        <div className="d-flex flex-wrap gap-3">
          <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 220 }}>
            <label className="form-label pt-1 start">Account Code</label>
            <Select
              menuPortalTarget={document.body}
              styles={selectStyles}
              options={accountOptions}
              value={form.account}
              onChange={handleAccountSelected}
              placeholder="Select Account"
            />
          </div>
          <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 220 }}>
            <label className="form-label pt-1 start">Credit</label>
            <input
              type="number"
              className="p-2 form-control"
              value={form.credit}
              onChange={(e) => setForm((prev) => ({ ...prev, credit: e.target.value }))}
              onBlur={handleCreditBlur}
            />
          </div>
          <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 220 }}>
            <label className="form-label pt-1 start">Tax</label>
            <Select
              menuPortalTarget={document.body}
              styles={selectStyles}
              options={taxOptions}
              value={form.tax}
              onChange={handleTaxSelected}
              placeholder="Select Tax"
              isClearable
            />
          </div>
          <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 220 }}>
            <label className="form-label pt-1 start">VAT</label>
            <input
              type="number"
              className="p-2 form-control"
              value={form.vat}
              onChange={(e) => handleVatChanged(e.target.value)}
            />
          </div>
          <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 220 }}>
            <label className="form-label pt-1 start">Total</label>
            <input type="text" disabled className="p-2 form-control" value={total.toFixed(2)} />
          </div>
          {!applySameCostCenters && (
            <>
              <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 220 }}>
                <label className="form-label pt-1 start">Cost Center 1</label>
                <Select
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  options={costCenter1.map((c) => ({ value: c.id, label: c.name }))}
                  value={form.cc1}
                  onChange={(o) => setForm((prev) => ({ ...prev, cc1: o }))}
                  isClearable
                />
              </div>
              <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 220 }}>
                <label className="form-label pt-1 start">Cost Center 2</label>
                <Select
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  options={costCenter2.map((c) => ({ value: c.id, label: c.name }))}
                  value={form.cc2}
                  onChange={(o) => setForm((prev) => ({ ...prev, cc2: o }))}
                  isClearable
                />
              </div>
              <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 220 }}>
                <label className="form-label pt-1 start">Cost Center 3</label>
                <Select
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  options={costCenter3.map((c) => ({ value: c.id, label: c.name }))}
                  value={form.cc3}
                  onChange={(o) => setForm((prev) => ({ ...prev, cc3: o }))}
                  isClearable
                />
              </div>
              <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 220 }}>
                <label className="form-label pt-1 start">Cost Center 4</label>
                <Select
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  options={costCenter4.map((c) => ({ value: c.id, label: c.name }))}
                  value={form.cc4}
                  onChange={(o) => setForm((prev) => ({ ...prev, cc4: o }))}
                  isClearable
                />
              </div>
            </>
          )}
        </div>
        <div className="mt-3 d-flex flex-column">
          <label className="form-label pt-1 start">Narration</label>
          <textarea
            className="p-2 form-control w-100"
            rows={3}
            value={form.narration}
            onChange={(e) => setForm((prev) => ({ ...prev, narration: e.target.value }))}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="mainbtn flex-end gap-2" onClick={handleSave}>
          <FaSave color="#fff" className="center mt-1 mx-2" />
          <div className="center">{editItem ? 'Update' : 'Add'}</div>
        </div>
        <div className="mainbtnRed flex-end gap-2" onClick={onClose}>
          <FaChevronLeft color="#fff" className="center mt-1 mx-2" />
          <div className="center">Cancel</div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default SaleVoucherItemForm;
